import '../styles/globals.css'
import '../styles/tooltip.css'
import Head from 'next/head';
import dynamic from 'next/dynamic';
import 'tippy.js/animations/shift-away.css';
import Router from "next/router";
import { Fragment, useEffect, useState } from "react";
import AOS from 'aos';
import 'aos/dist/aos.css';
import { config } from '@fortawesome/fontawesome-svg-core'
import '@fortawesome/fontawesome-svg-core/styles.css'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faArrowRight, faHome, faCompass, faLanguage } from '@fortawesome/free-solid-svg-icons';
import { K2D } from 'next/font/google';

const k2d = K2D({
  weight: ['100', '200', '300', '400', '500', '600', '700', '800'],
  style: ['normal', 'italic'],
  subsets: ['latin'],
  display: 'swap',
});

config.autoAddCss = false

const Transition = dynamic(() => import('@headlessui/react').then(mod => mod.Transition), { ssr: true });
const BottomNavBar = dynamic(() => import('../components/BottomNavBar'), { ssr: true });


const Header = dynamic(() => import('../components/Header'))

function MyApp({ Component, pageProps , enabled: serverEnabled, load: serverLoad}) {
  let [load, setLoad] = useState(true);
  // Initialisation à null, puis synchronisation avec localStorage au premier montage
  const [enabled, setEnabled] = useState();
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const theme = localStorage.getItem('theme');
      setEnabled(theme === 'dark');
    }
  }, []);
  const [isMobile, setIsMobile] = useState(false);
  const [showTranslateDropdown, setShowTranslateDropdown] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState('fr');
  // ...rien à cet endroit, la logique est déplacée dans BottomNavBar.jsx

  const languages = [
    { code: 'fr', name: 'Français', flag: '🇫🇷' },
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'es', name: 'Español', flag: '🇪🇸' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
    { code: 'zh', name: '中文', flag: '🇨🇳' },
    { code: 'ar', name: 'العربية', flag: '🇸🇦' }
  ];

  const translatePage = (langCode) => {
    if (langCode === 'fr') {
      // Ne pas recharger la page, juste fermer le dropdown
      setShowTranslateDropdown(false);
      return;
    }
    const googleTranslateUrl = `https://translate.google.com/translate?sl=fr&tl=${langCode}&u=${encodeURIComponent(window.location.href)}`;
    window.open(googleTranslateUrl, '_blank');
    setCurrentLanguage(langCode);
    setShowTranslateDropdown(false);
  };

  // Fermer le dropdown de traduction si on clique ailleurs
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showTranslateDropdown && !event.target.closest('.translate-dropdown-mobile')) {
        setShowTranslateDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showTranslateDropdown]);


  useEffect(() => {
    // Optimisation: utiliser une fonction de resize avec debounce
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    // Vérification initiale
    handleResize();

    // Ajouter le listener de resize avec debounce
    let resizeTimeout;
    const debouncedResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(handleResize, 150);
    };

    window.addEventListener('resize', debouncedResize, { passive: true });

    // Navigation au clavier optimisée - suppression des références aux services
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        if (window.location.pathname === '/') {
          Router.push('/projects');
        } else if (window.location.pathname === '/projects') {
          Router.push('/');
        }
      }
      if (e.key === 'ArrowRight') {
        if (window.location.pathname === '/') {
          Router.push('/projects');
        } else if (window.location.pathname === '/projects') {
          Router.push('/');
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    setTimeout(() => {
      setLoad(true);
      document.documentElement.style.pointerEvents = 'all';
    }, 150);

    // Cleanup function
    return () => {
      window.removeEventListener('resize', debouncedResize);
      document.removeEventListener('keydown', handleKeyDown);
      clearTimeout(resizeTimeout);
    };
  }, [])
  // Optimisation des événements de Router
  useEffect(() => {
    const handleRouteChangeStart = () => {
      setLoad(false);
      document.documentElement.style.pointerEvents = 'none';
    };

    const handleRouteChangeComplete = () => {
      setTimeout(() => {
        setLoad(true);
        document.documentElement.style.pointerEvents = 'all';
      }, 500);
    };

    Router.events.on('routeChangeStart', handleRouteChangeStart);
    Router.events.on('routeChangeComplete', handleRouteChangeComplete);
    Router.events.on('routeChangeError', handleRouteChangeComplete);

    return () => {
      Router.events.off('routeChangeStart', handleRouteChangeStart);
      Router.events.off('routeChangeComplete', handleRouteChangeComplete);
      Router.events.off('routeChangeError', handleRouteChangeComplete);
    };
  }, []);

  // Mise à jour des navItems avec le bouton de traduction pour mobile
  const navItems = [
    { icon: faHome, active: faHome, label: 'Accueil', href: '/' },
    { icon: faCompass, active: faCompass, label: 'Projets', href: '/projects' },
    { icon: faLanguage, active: faLanguage, label: 'Traduire', isTranslate: true }
  ]
  // Optimisation de la gestion du scroll et AOS
  useEffect(() => {
    AOS.init({
      duration: 800, // Animation douce et visible
      once: true,
      disable: 'mobile', // Désactive AOS sur mobile
    });
    // La gestion du scroll de la nav bar est maintenant dans BottomNavBar.jsx
  }, []);


  // Synchronisation du thème avec localStorage et application des classes
  useEffect(() => {
    if (typeof window === 'undefined') return;
    localStorage.setItem('theme', enabled ? 'dark' : 'light');
    const body = document.getElementById('page');
    const html = document.querySelector('html');
    if (!body || !html) return;
    if (enabled) {
      body.classList.remove('whitetheme');
      body.classList.add('blacktheme');
      html.classList.remove('whitetheme');
      html.classList.add('blacktheme');
      document.body.style.backgroundColor = '#000000';
    } else {
      body.classList.remove('blacktheme');
      body.classList.add('whitetheme');
      html.classList.remove('blacktheme');
      html.classList.add('whitetheme');
      document.body.style.backgroundColor = '#ffffff';
    }
  }, [enabled]);

  // Lecture du thème depuis localStorage à chaque navigation
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const updateTheme = () => {
      setEnabled(localStorage.getItem('theme') === 'dark');
    };
    Router.events.on('routeChangeComplete', updateTheme);
    return () => {
      Router.events.off('routeChangeComplete', updateTheme);
    };
  }, []);

  // Styles de transition avec flou stylisé selon le thème
  const transitionStyle = enabled ? {
    zIndex: 999999,
    background: "radial-gradient(circle, rgba(14,17,49,1) 0%, rgba(0,0,0,1) 75%, rgba(0,0,0,1) 100%)",
    backdropFilter: "blur(20px)",
    WebkitBackdropFilter: "blur(20px)",
  } : {
    zIndex: 999999,
    background: "radial-gradient(circle, rgba(232,214,139,1) 0%, rgba(255,255,255,1) 70%, rgba(255,255,255,1) 100%)",
    backdropFilter: "blur(15px)",
    WebkitBackdropFilter: "blur(15px)",
  }

  return (
    <div id='page' className={`${k2d.className} ${enabled ? 'blacktheme' : 'whitetheme'}`}>
      <Transition
        as={Fragment}
        show={!load ? true : false}
        className="fixed w-full h-screen flex justify-center items-center pointer-events-none"
        enter="transform transition duration-[1500ms] ease-out"
        enterFrom="opacity-0 scale-95"
        enterTo="opacity-100 scale-100"
        leave="transform duration-[1500ms] transition ease-in-out"
        leaveFrom="opacity-100 scale-100"
        leaveTo="opacity-0 scale-105"
      >
        <div className="fixed w-full h-screen flex justify-center items-center pointer-events-none" 
        style={transitionStyle}
        >
          <div className="flex items-center gap-x-6">
            <div className="text-center items-center justify-center flex flex-col">
              <div className={`animate-pulse ${enabled ? 'text-white' : 'text-black'}`}>
                <div className={`h-20 w-20 rounded-full mb-4 ${enabled ? 'bg-gradient-to-r from-orange-400 to-orange-600' : 'bg-gradient-to-r from-orange-300 to-orange-500'} shadow-2xl flex items-center justify-center`}>
                  <span className="text-2xl font-bold text-white">SDO</span>
                </div>
                <p className="text-2xl mb-2 font-thin">Etudiant IA et Big Data - Développeur Fullstack</p>
                <div className="flex justify-center space-x-1">
                  <div className={`h-2 w-2 rounded-full ${enabled ? 'bg-white' : 'bg-black'} animate-bounce`} style={{animationDelay: '0ms'}}></div>
                  <div className={`h-2 w-2 rounded-full ${enabled ? 'bg-white' : 'bg-black'} animate-bounce`} style={{animationDelay: '150ms'}}></div>
                  <div className={`h-2 w-2 rounded-full ${enabled ? 'bg-white' : 'bg-black'} animate-bounce`} style={{animationDelay: '300ms'}}></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Transition>
      <div className={`w-full h-5 flex items-center justify-center shadow-md transition-colors duration-300 ${enabled ? 'bg-black text-white' : 'bg-white text-black'}`}>
        <p className="text-xs font-bold ml-2">
          Utilisez les flèches <FontAwesomeIcon icon={faArrowLeft} className="mx-1" /> / <FontAwesomeIcon icon={faArrowRight} className="mx-1" /> pour naviguer entre les pages
        </p>
      </div>
      {load&& <main className="h-full w-full pt-2 main-container">
        <div className="absolute top-0 left-0">
        </div>
        <div className="min-h-screen max-w-screen-lg p-5 w-full md:w-10/12 lg:w-8/12 mx-auto transition-all duration-150 mobile-safe">
          <Header enabled={enabled} setEnabled={setEnabled} />
          <Component {...pageProps}  enabled={enabled} setEnabled={setEnabled} />
        </div>
        {isMobile && (
          <BottomNavBar
            enabled={enabled}
            currentLanguage={currentLanguage}
            setCurrentLanguage={setCurrentLanguage}
          />
        )}
        <div>
          <div className="max-w-screen-lg p-5 w-full md:w-10/12 lg:w-8/12 mx-auto transition-all duration-300 mobile-safe">
            <div className="md:flex w-full items-center justify-between">
              <div className="text-center md:text-left">
                <p> @ {new Date().getFullYear()} SOSSOU Didi Orlog -  Tous droits réservés</p>
                <p className="text-sm">Design inspiré de umutxyp - Personal-Website</p>
              </div>
            </div>
          </div>
        </div>
      </main>}
    </div>
  )
}

export async function getStaticProps() {
  // Données globales pour le SEO et la performance
  const globalData = {
    enabled: true,
    load: true,
    site: {
      name: "Didi Orlog SOSSOU",
      url: "https://www.orlogsd.dev",
      logo: "/assets/profile/rm.webp",
      description: "Développeur FullStack spécialisé en IA et Data Science"
    }
  }
  
  return {
    props: globalData
  }
}

export default MyApp
